import { ORPCError } from '@orpc/client'
import type {
  Invitation,
  TenantMember,
  TenantRole,
} from '@sirius/contracts/admin'
import {
  invitationCreateInputSchema,
  TENANT_GRANT_ROLES,
  tenantRoleSchema,
} from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import type { z } from 'zod'

/** The Invite dialog's fields: the API's input without the host tenant. */
export const inviteFormSchema = invitationCreateInputSchema.omit({
  tenantId: true,
})
export type InviteFormValues = z.input<typeof inviteFormSchema>
export type InviteFormOutput = z.output<typeof inviteFormSchema>

/**
 * The roles the Invite dialog offers: staff invite with any role, a
 * tenant's owners and admins as admin or member (the API refuses an owner).
 */
export function inviteRoleOptions(
  isPlatformAdmin: boolean,
): readonly TenantRole[] {
  return isPlatformAdmin ? tenantRoleSchema.options : TENANT_GRANT_ROLES
}

/**
 * A refused invite that the email field explains (a member of the tenant or
 * an open invitation: CONFLICT); null for any other error, which the dialog
 * shows as a whole.
 */
export function inviteFieldError(
  error: unknown,
  t: TFunction,
): { field: 'email'; message: string } | null {
  return error instanceof ORPCError && error.code === 'CONFLICT'
    ? {
        field: 'email',
        message: t('settingsPage.invite.emailTaken'),
      }
    : null
}

/** A role the Members tab gives (`members.setRole`). */
export type GrantRole = (typeof TENANT_GRANT_ROLES)[number]

/** Who looks at the Members tab: staff, or one of the tenant's users. */
export type MemberViewer = { isPlatformAdmin: boolean; userId: string }

/**
 * Whether the viewer may change the member's role and remove them: staff
 * every member, owners too; a tenant's owners and admins its admins and
 * members, never an owner nor themselves (the API refuses those). Nothing
 * while the viewer is unknown (loading).
 */
export function canManageMember(
  viewer: MemberViewer | undefined,
  member: Pick<TenantMember, 'userId' | 'role'>,
): boolean {
  if (viewer === undefined) {
    return false
  }
  return (
    viewer.isPlatformAdmin ||
    (member.role !== 'owner' && member.userId !== viewer.userId)
  )
}

/**
 * Whether the viewer may revoke the open invitation: staff every one, a
 * tenant's owners and admins all but an invitation as owner.
 */
export function canRevokeInvitation(
  isPlatformAdmin: boolean,
  invitation: Pick<Invitation, 'role'>,
): boolean {
  return isPlatformAdmin || invitation.role !== 'owner'
}

/**
 * The options of a member's role select: `TENANT_GRANT_ROLES`, and an
 * owner's own role shown but not offered (only `users.setMembership` makes
 * an owner).
 */
export function memberRoleOptions(
  role: TenantRole,
): { role: TenantRole; disabled: boolean }[] {
  const grant = TENANT_GRANT_ROLES.map((option) => ({
    role: option,
    disabled: false,
  }))
  return role === 'owner' ? [{ role, disabled: true }, ...grant] : grant
}

/** What the Members tab asks before it gives a member another role. */
export function roleChangeConfirmation(
  name: string,
  role: GrantRole,
  tenant: string,
  t: TFunction,
): { title: string; description: string } {
  return {
    title: t(`settingsPage.members.roleChange.title.${role}`, { name, tenant }),
    description: t(`settingsPage.members.roleChange.description.${role}`, {
      tenant,
    }),
  }
}

/** The React Query keys of the Members tab's reads, per tenant. */
export const membersQueryKey = (tenantId: string) =>
  ['tenantMembers', tenantId] as const
export const invitationsQueryKey = (tenantId: string) =>
  ['tenantInvitations', tenantId] as const
